import React, { useState } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Calendar from '../../components/common/Calendar';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';

const CompetitionsContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
`;

const CalendarSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const CalendarInfo = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 1rem;
  line-height: 1.6;
`;

const CompetitionsList = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const CompetitionsListTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1.5rem;
`;

const CompetitionCard = styled.div`
  padding: 1.5rem;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #000000;
    background: #f8f9fa;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const CompetitionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  gap: 1rem;
`;

const CompetitionTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: #000000;
  flex: 1;
`;

const CompetitionDate = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  color: #000000;
  white-space: nowrap;
`;

const CompetitionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const CompetitionDetail = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
`;

const ParticipantCount = styled.div`
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid #dee2e6;
  font-size: 0.875rem;
  color: #495057;
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #adb5bd;
  font-size: 1rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
    max-height: 80vh;
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.875rem;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #000000;
  }
  
  &::placeholder {
    color: #adb5bd;
  }
`;

const TextAreaLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.5rem;
  display: block;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

interface Competition {
  id: number;
  date: string;
  time: string;
  title: string;
  location: string;
  description: string;
  participantCount?: number;
}

const AdminCompetitions: React.FC = () => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompetition, setEditingCompetition] = useState<Competition | null>(null);
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    title: '',
    location: '',
    description: ''
  });

  // TODO: 백엔드 API에서 대회 일정 목록 가져오기
  // GET /api/competitions?year=2025&month=1
  const [competitions, setCompetitions] = useState<Competition[]>([
    { 
      id: 1, 
      date: '2025-01-20', 
      time: '14:00', 
      title: '신년 친선 대회',
      location: '수원체육관', 
      description: '새해를 맞아 친선 대회를 개최합니다.',
      participantCount: 12
    },
    { 
      id: 2, 
      date: '2025-02-15', 
      time: '13:00', 
      title: '겨울 리그전',
      location: '수원실내체육관', 
      description: '겨울 시즌 리그전입니다.',
      participantCount: 8
    },
  ]);

  const competitionDates = competitions.map(c => c.date);

  const handleDateClick = (dateStr: string) => {
    const existingCompetition = competitions.find(c => c.date === dateStr);
    
    if (existingCompetition) {
      // 기존 대회 수정
      setEditingCompetition(existingCompetition);
      setFormData({
        date: existingCompetition.date,
        time: existingCompetition.time,
        title: existingCompetition.title,
        location: existingCompetition.location,
        description: existingCompetition.description
      });
    } else {
      // 새 대회 추가
      setEditingCompetition(null);
      setFormData({
        date: dateStr,
        time: '14:00',
        title: '',
        location: '',
        description: ''
      });
    }
    
    setSelectedDate(dateStr);
    setIsModalOpen(true);
  };

  const handleAddClick = () => {
    setSelectedDate('');
    setEditingCompetition(null);
    setFormData({
      date: '',
      time: '14:00',
      title: '',
      location: '',
      description: ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingCompetition) {
      // TODO: 대회 수정 API
      // PUT /api/competitions/{id}
      setCompetitions(competitions.map(c => 
        c.id === editingCompetition.id 
          ? { 
              ...c, 
              date: formData.date,
              time: formData.time,
              title: formData.title,
              location: formData.location,
              description: formData.description
            }
          : c
      ));
    } else {
      // TODO: 대회 추가 API
      // POST /api/competitions
      const newCompetition: Competition = {
        id: competitions.length + 1,
        date: formData.date,
        time: formData.time,
        title: formData.title,
        location: formData.location,
        description: formData.description,
        participantCount: 0
      };
      setCompetitions([...competitions, newCompetition]);
    }
    
    setIsModalOpen(false);
    setSelectedDate('');
  };

  const handleDelete = () => {
    if (editingCompetition) {
      if (window.confirm('이 대회 일정을 삭제하시겠습니까?')) {
        // TODO: 대회 삭제 API
        // DELETE /api/competitions/{id}
        setCompetitions(competitions.filter(c => c.id !== editingCompetition.id));
        setIsModalOpen(false);
        setSelectedDate('');
      }
    }
  };

  const handleMonthChange = (newYear: number, newMonth: number) => {
    setCurrentYear(newYear);
    setCurrentMonth(newMonth);
    // TODO: 새로운 월의 대회 일정 불러오기
  };

  return (
    <DashboardLayout title="대회 일정 관리">
      <CompetitionsContainer>
        <Header>
          <Title>대회 일정 관리</Title>
          <Button variant="primary" onClick={handleAddClick}>
            + 대회 추가
          </Button>
        </Header>
        
        <CalendarSection>
          <Calendar
            year={currentYear}
            month={currentMonth}
            scheduledDates={competitionDates}
            selectedDate={selectedDate}
            onDateClick={handleDateClick}
            onMonthChange={handleMonthChange}
          />
          <CalendarInfo>
            ✓ 검은색 테두리 날짜는 대회가 있는 날입니다. 클릭하여 수정/삭제할 수 있습니다.
            <br />
            ✓ 빈 날짜를 클릭하면 새 대회를 추가할 수 있습니다.
          </CalendarInfo>
        </CalendarSection>
        
        <CompetitionsList>
          <CompetitionsListTitle>대회 목록</CompetitionsListTitle>
          {competitions.length > 0 ? (
            competitions
              .sort((a, b) => a.date.localeCompare(b.date))
              .map(competition => (
                <CompetitionCard key={competition.id} onClick={() => handleDateClick(competition.date)}>
                  <CompetitionHeader>
                    <CompetitionTitle>🏆 {competition.title}</CompetitionTitle>
                    <CompetitionDate>📅 {competition.date}</CompetitionDate>
                  </CompetitionHeader>
                  <CompetitionDetails>
                    <CompetitionDetail>🕐 {competition.time}</CompetitionDetail>
                    <CompetitionDetail>📍 {competition.location}</CompetitionDetail>
                    <CompetitionDetail>📝 {competition.description}</CompetitionDetail>
                  </CompetitionDetails>
                  <ParticipantCount>
                    👥 참가 신청: {competition.participantCount || 0}명
                  </ParticipantCount>
                </CompetitionCard>
              ))
          ) : (
            <EmptyState>등록된 대회가 없습니다.</EmptyState>
          )}
        </CompetitionsList>
        
        {/* 대회 추가/수정 모달 */}
        <ModalOverlay 
          isOpen={isModalOpen}
          onClick={() => setIsModalOpen(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingCompetition ? '대회 수정' : '대회 추가'}</ModalTitle>
              <CloseButton onClick={() => setIsModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            
            <ModalForm onSubmit={handleSubmit}>
              <Input
                label="대회명"
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="예: 신년 친선 대회"
                required
              />
              
              <Input
                label="날짜"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
              
              <Input
                label="시간"
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                required
              />
              
              <Input
                label="장소"
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="예: 수원체육관"
                required
              />
              
              <div>
                <TextAreaLabel>
                  설명 <span style={{ color: '#dc3545' }}>*</span>
                </TextAreaLabel>
                <TextArea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="대회에 대한 설명을 입력하세요"
                  required
                />
              </div>
              
              <ModalActions>
                {editingCompetition && (
                  <Button
                    type="button"
                    variant="danger"
                    size="large"
                    fullWidth
                    onClick={handleDelete}
                  >
                    삭제
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  fullWidth
                >
                  {editingCompetition ? '수정' : '추가'}
                </Button>
              </ModalActions>
            </ModalForm>
          </ModalContent>
        </ModalOverlay>
      </CompetitionsContainer>
    </DashboardLayout>
  );
};

export default AdminCompetitions;

